import { CalculationOutput } from "../types";
import { PeriodMode } from "../utils/period";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";

export default function CalculationBreakdown({
  output,
  periodMode,
  isPremium = true,
}: {
  output: CalculationOutput | null;
  periodMode: PeriodMode;
  isPremium?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (!output) return null;

  const fmt = (n: number) => {
    const value = periodMode === "monthly" ? n / 12 : n;
    return value.toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const suffix = periodMode === "monthly" ? "/mes" : "/año";

  const brutoFacturado = output.brutoFacturado ?? 0;
  const costesCash = output.costesCash ?? 0;
  const cuotaRETAAnual = output.cuotaRETAAnual ?? 0;
  const rendimientoNeto = output.rendimientoNeto ?? 0;
  const reduccionDFJ = output.reduccionDFJ ?? 0;
  const baseImponibleIRPF = output.baseImponibleIRPF ?? 0;
  const minimoPersonalUsado = output.minimoPersonalUsado ?? 0;
  const irpfEstatal = output.irpfEstatal ?? 0;
  const irpfAutonomica = output.irpfAutonomica ?? 0;
  const irpfTotal = output.irpfTotal ?? 0;
  const anticiposIRPF_retenciones = output.anticiposIRPF_retenciones ?? 0;
  const anticiposIRPF_mod130 = output.anticiposIRPF_mod130 ?? 0;
  const anticiposIRPF = output.anticiposIRPF ?? 0;
  const irpfRegularizacion = output.irpfRegularizacion ?? 0;
  const cajaPreviaRenta = output.cajaPreviaRenta ?? 0;
  const netoFinalTrasRenta = output.netoFinalTrasRenta ?? 0;

  return (
    <div
      style={{
        marginTop: "32px",
        borderTop: "1px solid var(--border)",
        paddingTop: "24px",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          appearance: "none",
          background: "none",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          textAlign: "left",
          fontSize: "16px",
          fontWeight: 700,
          color: "var(--text)",
          cursor: "pointer",
          padding: "12px 0",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--brand)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--text)";
        }}
      >
        <span>🧾 Detalle del cálculo</span>
        <span
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--muted)",
          }}
        >
          {open ? "Ocultar ▲" : "Mostrar ▼"}
        </span>
      </button>

      {open && (
        <div
          style={{
            marginTop: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "var(--radius-lg)",
            padding: "24px",
            border: "1px solid var(--border)",
            position: "relative",
          }}
        >
          {!isPremium && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(8px)",
                borderRadius: "var(--radius-lg)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                padding: "48px",
                textAlign: "center",
                zIndex: 10,
              }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "var(--brand-soft)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Lock size={32} color="var(--brand)" />
              </div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "var(--text)",
                }}
              >
                Auditoría completa en Premium
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "15px",
                  color: "var(--muted)",
                  maxWidth: "480px",
                  lineHeight: "1.6",
                }}
              >
                Accede al desglose detallado paso a paso de todos los cálculos fiscales, con explicaciones completas del IRPF, mínimos y deducciones aplicadas
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  appearance: "none",
                  padding: "12px 24px",
                  backgroundColor: "var(--brand)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  marginTop: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--brand-ink)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--brand)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Acceder a Premium
              </button>
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "var(--text)",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                1. Rendimiento neto:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Bruto facturado ({fmt(brutoFacturado)} €{suffix}) − Costes
                deducibles ({fmt(costesCash)} €{suffix}) − Cuota de autónomos (
                {fmt(cuotaRETAAnual)} €{suffix}) = <strong>{fmt(rendimientoNeto)} €{suffix}</strong>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                2. Base imponible IRPF:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Rendimiento neto ({fmt(rendimientoNeto)} €{suffix}) − Reducción
                actividad económica ({fmt(reduccionDFJ)} €{suffix}) ={" "}
                <strong>{fmt(baseImponibleIRPF)} €{suffix}</strong>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                3. Aplicación de mínimos personales y familiares:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Se descuenta un mínimo de {fmt(minimoPersonalUsado)} €{suffix}{" "}
                según edad y situación familiar. Este mínimo se aplica a la
                cuota, no a la base, calculando cuotas estatal y autonómica por
                separado.
              </div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                4. Cuota IRPF total:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Cuota estatal ({fmt(irpfEstatal)} €{suffix}) + Cuota autonómica
                ({fmt(irpfAutonomica)} €{suffix}) ={" "}
                <strong>{fmt(irpfTotal)} €{suffix}</strong>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                5. Anticipos IRPF:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Retenciones ({fmt(anticiposIRPF_retenciones)} €{suffix}) +
                Pagos modelo 130 ({fmt(anticiposIRPF_mod130)} €{suffix}) ={" "}
                <strong>{fmt(anticiposIRPF)} €{suffix}</strong>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                6. Regularización IRPF:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Cuota total ({fmt(irpfTotal)} €{suffix}) − Anticipos (
                {fmt(anticiposIRPF)} €{suffix}) ={" "}
                {irpfRegularizacion >= 0 ? (
                  <strong>
                    pago adicional de {fmt(irpfRegularizacion)} €{suffix}
                  </strong>
                ) : (
                  <strong>
                    devolución de {fmt(-irpfRegularizacion)} €{suffix}
                  </strong>
                )}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: "8px",
                  color: "var(--text)",
                }}
              >
                7. Neto final tras Renta:
              </div>
              <div style={{ color: "var(--muted)", marginLeft: "12px" }}>
                Caja previa a renta ({fmt(cajaPreviaRenta)} €{suffix}) −
                Regularización IRPF ({fmt(irpfRegularizacion)} €{suffix}) ={" "}
                <strong>{fmt(netoFinalTrasRenta)} €{suffix}</strong>
              </div>
            </div>

            <div
              style={{
                borderTop: "1px solid var(--border)",
                paddingTop: "16px",
                marginTop: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--muted)",
                  fontStyle: "italic",
                }}
              >
                Este resumen refleja la secuencia exacta de cálculo aplicada
                según el esquema LIRPF 2025, con tipos estatales y autonómicos
                vigentes. No sustituye la revisión profesional de una gestoría.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
